import { Box, Icon, Typography } from '@mui/material'
import { black, grey, shade } from '../../../colors'
import React, { useEffect, useState } from 'react'
import theme from '../../../theme'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { type IUser } from '../../../models/User'
import MultiSelectWithCheckBox from '../../../components/FormElements/MultiSelectWithCheckBox'
import { useFormContext } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import UserManager, { resetSurveyResponseForUser } from '../../../APIManager/UserManager'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CustomTooltip from '../../../components/CustomTooltip'
import GenericModal from '../../../components/GenericModal'
import { ButtonType } from '../../../components/GenericButton/styles'
import { notification } from '../../../components/Notifications'
import { SlotStatus } from '../../../models/DistributionPlan'

interface StudentEnrollmentListItemProps {
  index: number
  enrolled: boolean
  handlePressed: () => void
  readOnly?: boolean
  student: IUser
  sectionId?: string
  notEnrolledSection?: boolean
  slotId?: string
  sectionStatus?: string
}

export const StudentEnrollmentListItem: React.FC<StudentEnrollmentListItemProps> = ({
  index,
  enrolled,
  handlePressed,
  readOnly,
  student,
  sectionId,
  notEnrolledSection = false,
  slotId,
  sectionStatus
}) => {
  const backgroundColor = index % 2 === 0 ? 'none' : shade[1]
  const studentName = student.name
  const studentEmail = student?.email
  const [confirmResetSurveyModalOpen, setConfirmResetSurveyModalOpen] = useState(false)

  const { data: instructorsRaw = [], isLoading: instructorsLoading } = useQuery({
    queryKey: ['instructors', sectionId],
    queryFn: async () => {
      if (!sectionId) return []
      const res = await UserManager.getInstructorsBySectionIdApi(sectionId)
      return res.data || []
    },
    enabled: !!sectionId
  })

  const uniqueInstructors = React.useMemo(() => {
    const instructorsList: IUser[] = []
    const userIdSet = new Set<string>()
    instructorsRaw.forEach((section: any) => {
      if (Array.isArray(section.instructors)) {
        section.instructors.forEach((instructor: IUser) => {
          if (instructor.userId && !userIdSet.has(instructor.userId)) {
            userIdSet.add(instructor.userId)
            instructorsList.push(instructor)
          }
        })
      }
    })
    return instructorsList
  }, [instructorsRaw])

  const { setValue } = useFormContext()

  const studentEnrollment = React.useMemo(() => {
    if (Array.isArray(instructorsRaw) && instructorsRaw.length > 0) {
      return instructorsRaw.find((section: any) => section.user?.userId === student.userId) || null
    }
    return null
  }, [instructorsRaw, student.userId])

  const getInstructorTagsLabel = (value: IUser[]): string => {
    if (notEnrolledSection || studentEnrollment?.isEnrollmentForAllInstructors) {
      return 'All Instructors'
    }
    if (value.length === 0) {
      return 'No Instructor Selected'
    }
    return value.map((option) => option.name).join(', ')
  }

  useEffect(() => {
    if (studentEnrollment) {
      let selectedInstructors: IUser[] = []
      if (studentEnrollment.isEnrollmentForAllInstructors) {
        selectedInstructors = studentEnrollment.instructors || []
      } else {
        selectedInstructors = (studentEnrollment.instructors || []).filter((inst: any) => inst.isInstructorSelected)
      }
      setValue(`instructors-${student.userId}`, selectedInstructors)
    }
  }, [studentEnrollment, setValue, student.userId])

  const handleConfirmResetSurveyModal = async (): Promise<void> => {
    try {
      if (!slotId) {
        notification('The Survey Project instance is missing. Please try again.', { variant: 'error' })
        setConfirmResetSurveyModalOpen(false)
        return
      }

      if (!sectionId) {
        notification('The course section is missing. Please try again.', { variant: 'error' })
        setConfirmResetSurveyModalOpen(false)
        return
      }

      await resetSurveyResponseForUser(slotId, sectionId, student.userId)
      notification(`Successfully reset survey responses for ${studentName}`, { variant: 'success' })
      setConfirmResetSurveyModalOpen(false)
    } catch (error: any) {
      notification(error.message, { variant: 'error' })
      setConfirmResetSurveyModalOpen(false)
    }
  }
  // for testing purposes
  const canResetSurvey = readOnly && slotId && sectionStatus === SlotStatus.Live

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor,
        px: theme.spacing(4),
        borderRadius: theme.spacing(1),
        gap: theme.spacing(4),
        width: '100%'
        // flexWrap: 'nowrap'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: theme.spacing(2) }} width={'40%'}>
        <Typography
          variant="h6"
          fontWeight={700}
          fontSize={16}
          color={black[100]}
          lineHeight={1.4}
        >
          {studentName}
        </Typography>
        <Typography variant="body2" color="grey.500" fontSize={14}>
          {studentEmail}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing(1.5),
          mt: 5,
          width: '50%',
          maxWidth: '50%',
          py: theme.spacing(1)
        }}
      >
        <MultiSelectWithCheckBox
          options={uniqueInstructors || []}
          loading={instructorsLoading}
          label=""
          name={`instructors-${student.userId}`}
          placeholder={notEnrolledSection ? 'All Instructors Selected' : 'No Instructor Selected'}
          getOptionLabel={(option: IUser) => option?.name}
          getOptionSubLabel={(option: IUser) => {
            if (!option?.email) return ''
            const [local, domain] = option.email.split('@')
            return `${local} @${domain}`
          }}
          getOptionValue={(option: IUser) => option?.userId}
          showSelectAll
          clearIcon={false}
          fullWidth
          // @TODO We will fix this in the next sprint we will add disabled and readonly features
          // getOptionDisabled={(option: IUser) => !option.isActive}
          // disabled={notEnrolledSection}
          readonly={true}
          sx={{
            '& .MuiInputBase-root': {
              width: '210px',
              minWidth: '210px',
              maxWidth: '210px'
            }
          }}
          renderTags={(value) => (
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '150px',
                minWidth: '150px',
                maxWidth: '150px',
                px: theme.spacing(1),
                color: grey[400],
                fontWeight: 300
              }}
            >
              {getInstructorTagsLabel(value)}
            </Box>
          )}
        />

        <Box width={'8%'}>
          {!readOnly && (
            <Icon
              component={enrolled ? CloseIcon : AddIcon}
              color="action"
              onClick={handlePressed}
              sx={{
                cursor: 'pointer',
                fontSize: 24,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 0.6 },
                alignSelf: 'center',
                mb: 6
              }}
            />
          )}
          {canResetSurvey && (
            <CustomTooltip title={`Reset ${studentName}'s survey response`}>
              <RestartAltIcon
                sx={{
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  ml: theme.spacing(1),
                  '&:hover': { opacity: '80%' },
                  '&:active': { opacity: '60%' },
                  mb: 6
                }}
                onClick={() => { setConfirmResetSurveyModalOpen(true) }}
              />
            </CustomTooltip>
          )}
        </Box>
      </Box>
      <GenericModal
        isOpen={confirmResetSurveyModalOpen}
        onClose={() => { setConfirmResetSurveyModalOpen(false) }}
        title={`Reset Survey Responses for ${studentName}`}
        maxWidth='sm'
        confirmActionType={ButtonType.Destructive}
        onConfirm={async () => { await handleConfirmResetSurveyModal() }}
      >
        You are about to reset the survey responses from <strong>{studentName}</strong> for this course. This action cannot be undone.
      </GenericModal>
    </Box>
  )
}
